import React, { useState, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { Button, Icon, SectionHeading } from '@tviklax/ui-components';
import type { ShippingBillState, ShippingBillStatus } from '../hooks/useShippingBill';
import type { SchedulingStage } from './SchedulingContextTypes';
import { API_BASE_URL } from '../../../shared/constants/env';

const STEPS: { key: ShippingBillStatus[]; label: string }[] = [
  { key: ['drafting', 'drafted'], label: 'Draft SB' },
  { key: ['vessel_updated'], label: 'Vessel Added' },
  { key: ['filed'], label: 'Filed / ICEGATE' },
  { key: ['leo_granted'], label: 'LEO Granted' },
];

function stepIndex(status: ShippingBillStatus): number {
  if (status === 'not_started' || status === 'drafting') return -1;
  if (status === 'drafted') return 0;
  if (status === 'vessel_updated') return 1;
  if (status === 'filed') return 2;
  if (status === 'leo_granted') return 3;
  return -1;
}

interface ShippingBillCardProps {
  state: ShippingBillState;
  currentStage: SchedulingStage;
  isLoading?: boolean;
  error?: string | null;
  onGenerateDraft?: () => void;
  onMarkFiled?: () => void;
  onMarkLeoGranted?: () => void;
}

export const ShippingBillCard: React.FC<ShippingBillCardProps> = ({
  state,
  currentStage,
  isLoading,
  error,
  onGenerateDraft,
  onMarkFiled,
  onMarkLeoGranted,
}) => {
  const apiOrigin = API_BASE_URL.replace(/\/graphql\/?$/, '');
  const current = stepIndex(state.status);
  const isLoadingStage = currentStage === 'Loading';

  const downloadUrl = state.fileUrl
    ? state.fileUrl.startsWith('http')
      ? state.fileUrl
      : `${apiOrigin}${state.fileUrl.startsWith('/') ? '' : '/'}${state.fileUrl}`
    : null;

  const leoBlocked = isLoadingStage && state.status !== 'leo_granted';

  const [showPreview, setShowPreview] = useState(false);
  const [previewBlobUrl, setPreviewBlobUrl] = useState<string | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Revoke blob URL when modal closes to avoid memory leaks
  useEffect(() => {
    if (!showPreview && previewBlobUrl) {
      URL.revokeObjectURL(previewBlobUrl);
      setPreviewBlobUrl(null);
    }
  }, [showPreview]); // eslint-disable-line react-hooks/exhaustive-deps

  const openPreview = useCallback(async () => {
    if (!downloadUrl) return;
    setLoadingPreview(true);
    try {
      const resp = await fetch(downloadUrl);
      if (!resp.ok) throw new Error('Could not load PDF');
      const blob = await resp.blob();
      setPreviewBlobUrl(URL.createObjectURL(blob));
      setShowPreview(true);
    } catch {
      window.open(downloadUrl, '_blank', 'noopener,noreferrer');
    } finally {
      setLoadingPreview(false);
    }
  }, [downloadUrl]);

  return (
    <Card $leoBlocked={leoBlocked}>
      <SectionHeading
        icon="FileText"
        action={state.status !== 'not_started' && state.sbNumber ? <SbNumber>SB# {state.sbNumber}</SbNumber> : undefined}
      >
        Shipping Bill (ICEGate / Form SB-I)
      </SectionHeading>

      {leoBlocked && (
        <GateBanner>
          <Icon name="Lock" size={14} /> Cargo cannot enter yard/port until Shipping Bill is filed and LEO (Let Export Order) is issued by ICEGATE.
        </GateBanner>
      )}

      <Stepper>
        {STEPS.map((step, idx) => {
          const completed = idx <= current;
          const active = idx === current + 1 || (idx === 0 && current === -1 && state.status !== 'not_started');
          return (
            <React.Fragment key={step.label}>
              <StepItem>
                <StepDot $completed={completed} $active={active}>
                  {completed ? <Icon name="CheckCircle" size={14} /> : idx + 1}
                </StepDot>
                <StepLabel $completed={completed} $active={active}>
                  {step.label}
                </StepLabel>
              </StepItem>
              {idx < STEPS.length - 1 && <StepConnector $completed={idx < current} />}
            </React.Fragment>
          );
        })}
      </Stepper>

      {state.vesselName && (
        <MetaRow>
          <MetaLabel>Vessel</MetaLabel>
          <MetaValue>{state.vesselName} · {state.voyageNumber}</MetaValue>
        </MetaRow>
      )}

      {state.generatedAt && (
        <MetaRow>
          <MetaLabel>{state.status === 'drafted' ? 'Drafted' : 'Updated'}</MetaLabel>
          <MetaValue>{new Date(state.generatedAt).toLocaleString()}</MetaValue>
        </MetaRow>
      )}

      {state.filedAt && (
        <MetaRow>
          <MetaLabel>Filed at</MetaLabel>
          <MetaValue>{new Date(state.filedAt).toLocaleString()}</MetaValue>
        </MetaRow>
      )}

      {state.leoAt && (
        <MetaRow>
          <MetaLabel>LEO at</MetaLabel>
          <MetaValue>{new Date(state.leoAt).toLocaleString()}</MetaValue>
        </MetaRow>
      )}

      {error && (
        <ErrorText>
          <Icon name="AlertTriangle" size={13} /> {error}
        </ErrorText>
      )}

      <Actions>
        {state.status === 'not_started' && onGenerateDraft && (
          <Button size="sm" onClick={onGenerateDraft} disabled={isLoading}>
            {isLoading ? 'Generating...' : 'Generate Draft SB'}
          </Button>
        )}

        {downloadUrl && (
          <>
            <Button size="sm" variant="ghost" onClick={openPreview} disabled={loadingPreview}>
              {loadingPreview ? 'Loading...' : 'Preview PDF'}
            </Button>
            <a href={downloadUrl} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
              <Button size="sm" variant="ghost">Download ↓</Button>
            </a>
          </>
        )}

        {(state.status === 'drafted' || state.status === 'vessel_updated') && onMarkFiled && (
          <Button size="sm" variant="ghost" onClick={onMarkFiled}>
            Mark as Filed
          </Button>
        )}

        {state.status === 'filed' && onMarkLeoGranted && (
          <Button size="sm" onClick={onMarkLeoGranted}>
            Mark LEO Granted
          </Button>
        )}
      </Actions>

      {showPreview && previewBlobUrl && (
        <PreviewOverlay onClick={() => setShowPreview(false)}>
          <PreviewModal onClick={(e) => e.stopPropagation()}>
            <PreviewHeader>
              <PreviewTitle>Shipping Bill Preview</PreviewTitle>
              <Button size="sm" variant="ghost" onClick={() => setShowPreview(false)}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <Icon name="X" size={13} /> Close
                </span>
              </Button>
            </PreviewHeader>
            <iframe
              src={previewBlobUrl}
              title="Shipping Bill PDF"
              style={{ flex: 1, border: 'none', width: '100%' }}
            />
          </PreviewModal>
        </PreviewOverlay>
      )}
    </Card>
  );
};

const Card = styled.div<{ $leoBlocked: boolean }>`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  box-shadow: ${({ theme }) => theme.shadows.md};
  padding: ${({ theme }) => theme.spacing[5]};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
  border-left: 4px solid ${({ $leoBlocked, theme }) => ($leoBlocked ? theme.colors.error : theme.colors.primary)};
`;

const SbNumber = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.gray500};
  font-family: monospace;
`;

const GateBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${({ theme }) => theme.colors.errorLight};
  border: 1px solid ${({ theme }) => theme.colors.error};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing[3]};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.errorDark};
  font-weight: 500;
`;

const Stepper = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  padding: ${({ theme }) => theme.spacing[2]} 0;
`;

const StepItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

const StepDot = styled.div<{ $completed: boolean; $active: boolean }>`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  background: ${({ $completed, $active, theme }) =>
    $completed ? theme.colors.primary : $active ? theme.colors.primaryLight + '20' : theme.colors.gray100};
  color: ${({ $completed, $active, theme }) =>
    $completed ? theme.colors.surface : $active ? theme.colors.primary : theme.colors.gray400};
  border: 2px solid ${({ $completed, $active, theme }) =>
    $completed ? theme.colors.primary : $active ? theme.colors.primary : theme.colors.gray200};
  transition: all 0.2s;
`;

const StepLabel = styled.div<{ $completed: boolean; $active: boolean }>`
  font-size: 11px;
  font-weight: ${({ $completed, $active }) => ($completed || $active ? 600 : 400)};
  color: ${({ $completed, $active, theme }) =>
    $completed ? theme.colors.primary : $active ? theme.colors.primaryDark : theme.colors.gray400};
  white-space: nowrap;
`;

const StepConnector = styled.div<{ $completed: boolean }>`
  flex: 1;
  height: 2px;
  background: ${({ $completed, theme }) => ($completed ? theme.colors.primary : theme.colors.gray200)};
  margin-bottom: 18px;
  min-width: 24px;
`;

const MetaRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  font-size: 13px;
`;

const MetaLabel = styled.span`
  color: ${({ theme }) => theme.colors.gray500};
  min-width: 72px;
`;

const MetaValue = styled.span`
  color: ${({ theme }) => theme.colors.gray800};
  font-weight: 500;
`;

const ErrorText = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.error};
`;

const Actions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[2]};
  flex-wrap: wrap;
  margin-top: ${({ theme }) => theme.spacing[1]};
`;

const PreviewOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const PreviewModal = styled.div`
  width: min(900px, 95vw);
  height: 90vh;
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  box-shadow: ${({ theme }) => theme.shadows.xl};
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const PreviewHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing[4]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray200};
  flex-shrink: 0;
`;

const PreviewTitle = styled.h3`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.gray800};
`;
